import { AGENT_TRACKER_ROLE, ENGAGEMENT_TYPES } from "vue-app/types/enums";

const getStartedPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/ready-flows/${engagementName}`,
  component: () => import("../../assets/js/app/twig/TwigPageContainer.vue"),
  props: () => ({
    pageTitle: "GET STARTED",
    pageDesc: "See how this scenario can help you attract customers",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const adjustPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/adjust`,
  component: () => import("../../assets/js/app/twig/TwigPageContainer.vue"),
  props: () => ({
    pageTitle: "Adjust",
    pageDesc: "Customize your campaign to boost overall conversion",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/parameters`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const languagePageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/language`,
  component: () => import("../../assets/js/app/twig/TwigPageContainer.vue"),
  props: () => ({
    pageTitle: "Language",
    pageDesc:
      "eCommerce is global and your customers are everywhere. Language options allow you to speak with them in the most familiar way possible.",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/language`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const senderPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/sender`,
  component: () => import("../../assets/js/app/twig/TwigPageContainer.vue"),
  props: () => ({
    pageTitle: "Sender",
    pageDesc: "Use the ability to change sender names to best suit the kind of communication you are sharing.",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/sender`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const designPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/design`,
  component: () => import("../../assets/js/app/engagements/design/EngagementDesignPage.vue"),
  props: () => ({
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/design`,
    engagementName,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const runPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/run`,
  component: () => import("../../assets/js/app/twig/TwigPageContainer.vue"),
  props: () => ({
    pageTitle: "Run",
    pageDesc: "Allow agents to check for bugs and run scenarios.",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/run`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const reportsPageFactory = ({ engagementType, engagementName, isTransactional, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/reports`,
  component: () => import("../../assets/js/app/engagements/reports/EngagementReport.vue"),
  props: () => ({
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    skipGlobalLoader: true,
    engagementTypeId: engagementType,
    isGlobalNewslettersReport: false,
    isTransactionalEngagement: isTransactional,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
  },
});

const engagementBlockedPageFactory = ({ engagementType, engagementName, pageName, pageTitle, routeName }) => ({
  path: `/app/ready-flows/${engagementName}/${pageName}`,
  component: () => import("../../assets/js/app/twig/TwigPageContainer.vue"),
  props: () => ({
    pageTitle,
    pageDesc: "Make sure that engagement configuration is correct",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/${pageName}`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const referAFriend = [
  {
    path: "/app/ready-flows/refer-a-friend",
    component: () => import("vue-app/pages/PageReadyFlowLegacy.vue"),
    props: () => ({
      engagementName: "refer-a-friend",
      engagementTypeId: 20,
    }),
    meta: {
      skipGlobalLoader: true,
    },
    children: [
      {
        path: "/app/ready-flows/refer-a-friend",
        component: () => import("../../assets/js/app/engagements/referAFriend/ReferAFriendGetStarted.vue"),
        props: () => ({
          pageHelper: { name: "refer-a-friend" },
        }),
        meta: {
          routeName: window.$_t("REFER-A-FRIEND"),
          legacyLayout: true,
          engagementTypeId: ENGAGEMENT_TYPES.REFER_A_FRIEND,
          noPadding: true,
          skipGlobalLoader: true,
        },
      },
      {
        path: "/app/ready-flows/refer-a-friend/opt-in",
        component: () => import("../../assets/js/app/engagements/referAFriend/ReferAFriendOptIn.vue"),
        props: () => ({
          pageHelper: { name: "refer-a-friend" },
        }),
        meta: {
          routeName: window.$_t("REFER-A-FRIEND"),
          legacyLayout: true,
          engagementTypeId: ENGAGEMENT_TYPES.REFER_A_FRIEND,
          noPadding: true,
          skipGlobalLoader: true,
        },
      },
      adjustPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      languagePageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      senderPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      designPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      {
        path: "/app/ready-flows/refer-a-friend/popup",
        component: () => import("vue-app-new/pages/PageOnsitePopups/PageOnsitePopups.vue"),
        props: () => ({
          showHeader: false,
        }),
        meta: {
          routeName: window.$_t("REFER-A-FRIEND"),
          engagementTypeId: ENGAGEMENT_TYPES.REFER_A_FRIEND,
          skipGlobalLoader: true,
          noPadding: true,
        },
      },
      runPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      reportsPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
        isTransactional: true,
      }),
    ],
  },
];

const chatSubscription = [
  {
    path: "/app/ready-flows/chat-subscription",
    component: () => import("vue-app/pages/PageReadyFlowLegacy.vue"),
    props: () => ({
      engagementName: "chat-subscription",
      engagementTypeId: 18,
    }),
    meta: {
      skipGlobalLoader: true,
    },
    children: [
      getStartedPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      adjustPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      languagePageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      senderPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      designPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      runPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      reportsPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
        isTransactional: true,
      }),
    ],
  },
];

const crossSellingTransactional = [
  {
    path: "/app/ready-flows/cross-selling-transactional",
    component: () => import("vue-app/pages/PageReadyFlowLegacy.vue"),
    props: () => ({
      engagementName: "cross-selling-transactional",
      engagementTypeId: 16,
    }),
    meta: {
      skipGlobalLoader: true,
    },
    children: [
      getStartedPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      {
        path: `/app/ready-flows/cross-selling-transactional/adjust`,
        component: () => import("../../assets/js/app/engagements/cross-selling/CrossSellingAdjust.vue"),
        meta: {
          routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
          legacyLayout: true,
          engagementTypeId: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
          requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
          noPadding: true,
          skipGlobalLoader: true,
        },
      },
      languagePageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      senderPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      designPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      runPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      reportsPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
        isTransactional: true,
      }),
      engagementBlockedPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
        pageTitle: "Design",
        pageName: "missing-rules",
      }),
    ],
  },
];

const newEngagements = [
  {
    path: "/app/ready-flows/loyalty-program",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "loyalty-program",
      engagementTypeId: 2,
    }),
    meta: {
      legacyHref: "/app/engagements/loyalty-program",
      routeName: window.$_t("LOYALTY_PROGRAM"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/recommendations",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "recommendations",
      engagementTypeId: 15,
    }),
    meta: {
      routeName: window.$_t("RECOMMENDATIONS"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/birthday",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "birthday",
      engagementTypeId: 19,
    }),
    meta: {
      routeName: window.$_t("BIRTHDAY"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/wish-list",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "wish-list",
      engagementTypeId: 12,
    }),
    meta: {
      routeName: window.$_t("WISH-LIST"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/after-sale",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "after-sale",
      engagementTypeId: 9,
    }),
    meta: {
      routeName: window.$_t("AFTER SALE"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/advanced-restore-customers",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "advanced-restore-customers",
      engagementTypeId: 7,
    }),
    meta: {
      routeName: window.$_t("ADVANCED RESTORE CUSTOMERS"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/restore-customers",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "restore-customers",
      engagementTypeId: 1,
    }),
    meta: {
      routeName: window.$_t("RESTORE CUSTOMERS"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/recover-carts",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "recover-carts",
      engagementTypeId: 5,
    }),
    meta: {
      routeName: window.$_t("RECOVER CARTS"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/sms-subscription",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "sms-subscription",
      engagementTypeId: 31,
    }),
    meta: {
      routeName: window.$_t("SMS SUBSCRIPTION"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/newsletter-signup-after-purchase",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "newsletter-signup-after-purchase",
      engagementTypeId: 23,
    }),
    meta: {
      routeName: window.$_t("NEWSLETTER SIGNUP AFTER PURCHASE"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/price-change-alert-up",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "price-change-alert-up",
      engagementTypeId: 24,
    }),
    meta: {
      routeName: window.$_t("PRICE CHANGE ALERT UP"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/price-change-alert-down",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "price-change-alert-down",
      engagementTypeId: 25,
    }),
    meta: {
      routeName: window.$_t("PRICE CHANGE ALERT DOWN"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/newest-products",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "newest-products",
      engagementTypeId: 27,
    }),
    meta: {
      routeName: window.$_t("NEWEST PRODUCTS"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/low-stock-products",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "low-stock-products",
      engagementTypeId: 26,
    }),
    meta: {
      routeName: window.$_t("LOW STOCK PRODUCTS"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/ask-for-review",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "ask-for-review",
      engagementTypeId: 28,
    }),
    meta: {
      routeName: window.$_t("ASK FOR REVIEW"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/back-in-stock",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "back-in-stock",
      engagementTypeId: 29,
    }),
    meta: {
      routeName: window.$_t("BACK IN STOCK"),
      noPadding: true,
    },
  },
  {
    path: "/app/ready-flows/newsletter-subscription",
    component: () => import("vue-app/pages/PageReadyFlow.vue"),
    props: () => ({
      engagementName: "newsletter-subscription",
      engagementTypeId: 10,
    }),
    meta: {
      routeName: window.$_t("NEWSLETTER SUBSCRIPTION"),
      noPadding: true,
    },
  },
];

export const readyFlowsRoutes = [
  {
    path: "/app/ready-flows",
    component: () => import("../pages/PageReadyFlows/PageReadyFlows.vue"),
    meta: {
      routeName: window.$_t("ENGAGEMENTS"),
      requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
      noPadding: true,
      skipGlobalLoader: true,
    },
  },
  {
    path: "/app/ready-flows/attributions",
    props: (route) => ({
      firstDate: route.query ? route.query.firstDate : null,
      lastDate: route.query ? route.query.lastDate : null,
      engagementId: route.query ? route.query.engagementId : null,
      engagementTypeId: route.query ? route.query.engagementTypeId : null,
      isSummary: Boolean(route.query && route.query.engagementTypeId),
    }),
    component: () => import("../../assets/js/app/engagements/attributions/EngagementAttributions.vue"),
    meta: {
      routeName: window.$_t("Attributions"),
      legacyLayout: true,
      requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
      skipGlobalLoader: true,
    },
  },
  ...referAFriend,
  ...crossSellingTransactional,
  ...chatSubscription,
  ...newEngagements,
];
